import { useCallback, useEffect, useRef } from "react";
import {
    createSafeBlobUrl,
    createSafeVideoElement,
    type SafeBlobUrlManager,
    type SafeVideoElementManager
} from "@/utils/safeBlobUtils";

/**
 * React hook for managing a single blob URL with Safari-safe cleanup
 */
export function useSafeBlobUrl() {
    const managerRef = useRef<SafeBlobUrlManager | null>(null);

    const createUrl = useCallback((file: File): string | null => {
        // Clean up any existing manager
        if (managerRef.current && !managerRef.current.isCleanedUp) {
            managerRef.current.safeCleanup();
        }

        // Create new manager
        managerRef.current = createSafeBlobUrl(file);
        return managerRef.current.url;
    }, []);

    const cleanup = useCallback(() => {
        if (managerRef.current && !managerRef.current.isCleanedUp) {
            managerRef.current.safeCleanup();
        }
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanup();
        };
    }, [cleanup]);

    return {
        createUrl,
        cleanup,
        get url() {
            return managerRef.current?.url || null;
        },
        get isCleanedUp() {
            return managerRef.current?.isCleanedUp || true;
        }
    };
}

/**
 * React hook for managing a video element with Safari-safe blob URL cleanup
 */
export function useSafeVideoElement() {
    const managerRef = useRef<SafeVideoElementManager | null>(null);

    const createVideo = useCallback(
        (
            file: File,
            options?: {
                muted?: boolean;
                playsInline?: boolean;
                crossOrigin?: string;
                preload?: string;
            }
        ): HTMLVideoElement | null => {
            // Clean up any existing manager
            if (managerRef.current && !managerRef.current.isCleanedUp) {
                managerRef.current.cleanup();
            }

            // Create new manager
            managerRef.current = createSafeVideoElement(file, options);
            return managerRef.current.element;
        },
        []
    );

    const cleanup = useCallback(() => {
        if (managerRef.current && !managerRef.current.isCleanedUp) {
            managerRef.current.cleanup();
        }
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanup();
        };
    }, [cleanup]);

    return {
        createVideo,
        cleanup,
        get element() {
            return managerRef.current?.element || null;
        },
        get url() {
            return managerRef.current?.blobManager.url || null;
        },
        get isCleanedUp() {
            return managerRef.current?.isCleanedUp || true;
        },
        manager: managerRef
    };
}

/**
 * React hook for managing multiple blob URLs with Safari-safe cleanup
 */
export function useSafeBlobUrlSet() {
    const activeUrlsRef = useRef<Set<string>>(new Set());

    const add = useCallback((file: File): string => {
        const url = URL.createObjectURL(file);
        activeUrlsRef.current.add(url);
        return url;
    }, []);

    const remove = useCallback((url: string) => {
        if (activeUrlsRef.current.has(url)) {
            activeUrlsRef.current.delete(url);
            // Use delayed cleanup for Safari
            setTimeout(() => {
                try {
                    URL.revokeObjectURL(url);
                } catch (error) {
                    console.warn("Error revoking blob URL:", error);
                }
            }, 3000);
        }
    }, []);

    const cleanup = useCallback(() => {
        // Clean up all remaining URLs with Safari-safe delay
        setTimeout(() => {
            activeUrlsRef.current.forEach((url) => {
                try {
                    URL.revokeObjectURL(url);
                } catch (error) {
                    console.warn("Error revoking blob URL during cleanup:", error);
                }
            });
            activeUrlsRef.current.clear();
        }, 3000);
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanup();
        };
    }, [cleanup]);

    return {
        add,
        remove,
        cleanup,
        get size() {
            return activeUrlsRef.current.size;
        }
    };
}
